import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Space, Typography, message } from 'antd';
import { Link, useLocation } from '@umijs/max';
import { listMyApiKeys, renewMyApiKey } from '@/services/login';

const DAY = 24 * 60 * 60 * 1000;
const AUTO_REFRESH_CACHE_MS = 30 * 60 * 1000;
export const MAX_PERSONAL_KEYS = 2;
const PersonalApiKeysContext = createContext(null);

export function formatKeyExpiry(expiresAt, now) {
  if (!expiresAt) return '永不过期';
  const remaining = new Date(expiresAt).getTime() - now;
  if (!Number.isFinite(remaining)) return '有效期未知';
  if (remaining <= 0) return '已过期';
  if (remaining < DAY) return '剩余不足 24 小时';
  return `剩余 ${Math.ceil(remaining / DAY)} 天`;
}

export function formatKeyExpiryDate(expiresAt) {
  const date = new Date(expiresAt);
  if (!expiresAt || Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('zh-CN', { timeZoneName: 'short' });
}

export function PersonalApiKeysProvider({ enabled, children }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [pendingIds, setPendingIds] = useState([]);
  const [now, setNow] = useState(Date.now);
  const requestVersion = useRef(0);
  const lastFetchedAt = useRef(null);
  const inFlight = useRef(null);
  const pending = useRef(new Set());
  const mounted = useRef(false);

  const fetchKeys = useCallback(
    async (force = false) => {
      if (!enabled) return;
      if (!force) {
        if (inFlight.current) return inFlight.current.promise;
        if (
          lastFetchedAt.current !== null &&
          Date.now() - lastFetchedAt.current < AUTO_REFRESH_CACHE_MS
        )
          return;
      }
      // Explicit refreshes supersede reads started before a key mutation.
      lastFetchedAt.current = null;
      const version = ++requestVersion.current;
      setLoading(true);
      const request = { promise: null };
      inFlight.current = request;
      request.promise = (async () => {
        try {
          const data = await listMyApiKeys();
          if (mounted.current && version === requestVersion.current) {
            lastFetchedAt.current = Date.now();
            setKeys(data || []);
            setError(false);
            setNow(Date.now());
          }
        } catch (e) {
          if (mounted.current && version === requestVersion.current) setError(true);
        } finally {
          if (inFlight.current === request) inFlight.current = null;
          if (mounted.current && version === requestVersion.current) setLoading(false);
        }
      })();
      return request.promise;
    },
    [enabled],
  );

  // Key mutations and manual retry always invalidate the automatic-refresh cache.
  const refresh = useCallback(() => fetchKeys(true), [fetchKeys]);

  useEffect(() => {
    mounted.current = true;
    fetchKeys();
    const onReturn = () => {
      if (document.visibilityState !== 'hidden') {
        setNow(Date.now());
        fetchKeys();
      }
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
      lastFetchedAt.current = null;
      inFlight.current = null;
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [fetchKeys]);

  // Wake only at a display boundary; this never triggers a network request.
  useEffect(() => {
    const boundaries = keys
      .flatMap(({ expiresAt }) => {
        const expiry = expiresAt ? new Date(expiresAt).getTime() : NaN;
        const remaining = expiry - now;
        if (!Number.isFinite(remaining) || remaining <= 0) return [];
        return [
          expiry,
          expiry - 7 * DAY,
          expiry - DAY + 1,
          expiry - (Math.ceil(remaining / DAY) - 1) * DAY,
        ];
      })
      .filter((boundary) => boundary > now);
    if (!boundaries.length) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(...boundaries) - now);
    return () => clearTimeout(timer);
  }, [keys, now]);

  const renew = useCallback(
    async (id) => {
      if (!enabled || pending.current.has(id)) return;
      pending.current.add(id);
      setPendingIds([...pending.current]);
      // A previously started list response must not overwrite the renewed expiry.
      requestVersion.current += 1;
      lastFetchedAt.current = null;
      inFlight.current = null;
      setLoading(false);
      try {
        const renewed = await renewMyApiKey(id);
        if (!mounted.current) return;
        requestVersion.current += 1;
        setKeys((previous) =>
          previous.map((key) => (key.id === id ? { ...key, ...renewed } : key)),
        );
        setNow(Date.now());
        message.success(
          `已续期至 ${formatKeyExpiryDate(renewed.expiresAt)}，现有 MCP 配置可继续使用，无需更换 Key。`,
        );
        await refresh();
      } catch (e) {
        if (mounted.current) {
          message.error(e?.response?.data?.message || '续期失败，有效期未更新，请重试');
        }
      } finally {
        pending.current.delete(id);
        if (mounted.current) setPendingIds([...pending.current]);
      }
    },
    [enabled, refresh],
  );

  return (
    <PersonalApiKeysContext.Provider
      value={{
        keys,
        loading,
        error,
        pendingIds,
        now,
        refresh,
        renew,
        enabled,
        activeKeyCount: keys.filter(
          (key) => !key.expiresAt || new Date(key.expiresAt).getTime() > now,
        ).length,
      }}
    >
      {children}
    </PersonalApiKeysContext.Provider>
  );
}

export const usePersonalApiKeys = () => useContext(PersonalApiKeysContext);

export function RenewPersonalApiKeyButton({ apiKey }) {
  const { renew, pendingIds, enabled, now, activeKeyCount } = usePersonalApiKeys();
  const atLimit =
    activeKeyCount >= MAX_PERSONAL_KEYS &&
    apiKey.expiresAt &&
    new Date(apiKey.expiresAt).getTime() <= now;
  if (!enabled) return null;
  return (
    <Button
      size="small"
      loading={pendingIds.includes(apiKey.id)}
      disabled={pendingIds.includes(apiKey.id) || !!atLimit}
      title={
        atLimit
          ? '最多保留 2 个有效 ERP Key，请先吊销不再使用的 Key'
          : '将有效期设为从现在起 30 天，多次续期不会累加'
      }
      aria-label={`为 ${apiKey.name}（${apiKey.keyPrefix}）续期 30 天`}
      onClick={() => renew(apiKey.id)}
    >
      续期 30 天
    </Button>
  );
}

export function PersonalApiKeyReminder() {
  const { pathname } = useLocation();
  const { keys, now, error, refresh, loading, enabled } = usePersonalApiKeys();
  if (!enabled || pathname.replace(/\/$/, '') === '/account/devices') return null;
  const expiringKeys = keys.filter(
    (key) => key.expiresAt && new Date(key.expiresAt).getTime() - now <= 7 * DAY,
  );
  if (!error && !expiringKeys.length) return null;
  return (
    <Alert
      style={{ margin: '0 16px 16px' }}
      type="warning"
      showIcon
      message="个人 MCP Key 有效期提醒"
      description={
        <Space direction="vertical" style={{ width: '100%' }}>
          {error && (
            <Space wrap>
              <Typography.Text>
                暂时无法更新 Key 有效期，请重试；已有信息可能不是最新。
              </Typography.Text>
              <Button size="small" loading={loading} onClick={refresh}>
                重试
              </Button>
            </Space>
          )}
          {expiringKeys.map((key) => (
            <Space key={key.id} wrap>
              <Typography.Text strong>{key.name}</Typography.Text>
              <Typography.Text code>{key.keyPrefix}...</Typography.Text>
              <Typography.Text>{formatKeyExpiry(key.expiresAt, now)}</Typography.Text>
              <Typography.Text type="secondary">
                有效期至 {formatKeyExpiryDate(key.expiresAt)}
              </Typography.Text>
              <RenewPersonalApiKeyButton apiKey={key} />
            </Space>
          ))}
          <Space wrap>
            <Typography.Text type="secondary">
              续期后从现在起有效 30 天，无需更换 MCP 配置中的 Key。
            </Typography.Text>
            <Link to="/account/devices">管理设备与个人 Key</Link>
          </Space>
        </Space>
      }
    />
  );
}
